import { useEffect, useMemo, useState } from "react";
import {
  Space,
  Grid,
  Button,
  Row,
  Col,
  Card,
  Typography,
  Flex,
  Select,
} from "antd";
import { ClockCircleOutlined, PhoneOutlined } from "@ant-design/icons";

import { getStoreLocationsItems } from "../../../Components/Common/storeLocationsData";
import PageTitle from "../../../Components/PageTitle/pageTitle";

import WazeIcon from "../../../Images/Common/waze.png";
import GoogleMapsIcon from "../../../Images/Common/google-map.png";

const { useBreakpoint } = Grid;
const { Title } = Typography;

const StoreLocations = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isTablet = screens.md && !screens.lg;

  const [activeState, setActiveState] = useState("All");
  const [storeLocationsData, setStoreLocationsData] = useState([]);
  const [storeLocationsCategories, setStoreLocationsCategories] = useState([]);

  useEffect(() => {
    let storeLocations = getStoreLocationsItems() || [];

    setStoreLocationsData(storeLocations);

    setStoreLocationsCategories([
      "All", // Add "All" as the first element
      ...new Set(
        storeLocations.filter((s) => s.category).map((s) => s.category)
      ),
    ]);
  }, []);

  const stores = useMemo(() => {
    if (activeState === "All") return storeLocationsData;

    return storeLocationsData.filter(
      (s) => (s.category || "").toLowerCase() === activeState.toLowerCase()
    );
  }, [storeLocationsData, activeState]);

  return (
    <Space direction="vertical" className="page-container">
      <PageTitle
        title="Find a Teamstar Store Near You"
        subTitle="Easily locate any of our retail outlets across Malaysia."
      />

      <Row
        gutter={[16, 16]}
        style={{
          paddingLeft: isMobile || isTablet ? 0 : 20,
        }}
      >
        {/* LEFT states list (desktop only) */}
        {!isMobile && !isTablet && (
          <Col md={6} lg={5} xl={4}>
            <Space
              direction="vertical"
              style={{
                marginTop: 10,
                width: "100%",
                paddingBottom: 8,
              }}
            >
              <p
                style={{
                  color: "#002753",
                  fontSize: 18,
                  margin: "10px 0 8px 8px",
                  textAlign: "left",
                }}
              >
                <b>States</b>
              </p>

              {storeLocationsCategories.map((state) => {
                const isActive = activeState === state;

                return (
                  <Button
                    key={state}
                    onClick={() => setActiveState(state)}
                    style={{
                      color: isActive ? "#002753" : "#4B5563",
                      borderColor: isActive ? "#FFD600" : "transparent",
                      backgroundColor: isActive ? "#FFD600" : "unset",
                      fontSize: 16,
                      fontWeight: isActive ? 600 : 400,
                      boxShadow: "none",
                      textAlign: "left",
                      paddingTop: 18,
                      paddingBottom: 18,
                    }}
                  >
                    <span style={{ marginLeft: -5, width: 130 }}>
                      {" "}
                      {state}{" "}
                    </span>
                  </Button>
                );
              })}
            </Space>
          </Col>
        )}

        {/* RIGHT content */}
        <Col xs={24} md={24} lg={19} xl={20}>
          <Space
            direction="vertical"
            style={{
              marginTop: 10,
              width: "100%",
              paddingTop: isMobile ? 10 : 16,
              paddingRight: isMobile ? 10 : 16,
              paddingBottom: isMobile ? 10 : 16,
              paddingLeft: isMobile ? 10 : 0,
            }}
          >
            {/* Mobile + Tablet state selector */}
            {(isMobile || isTablet) && (
              <Select
                value={activeState}
                onChange={(v) => setActiveState(v)}
                options={storeLocationsCategories.map((s) => ({
                  label: s,
                  value: s,
                }))}
                style={{ width: "100%", marginBottom: 8 }}
              />
            )}

            <Title
              level={4}
              style={{
                color: "#002753",
                margin: isMobile ? "2px 0 8px" : "0 0 12px",
                marginTop: isMobile ? 10 : "auto",
                textTransform: "uppercase",
              }}
            >
              {activeState}
            </Title>

            <Row gutter={[16, 16]}>
              {stores.map((store) => (
                <Col
                  key={store.key}
                  xs={24} // 1 per row
                  sm={12} // 2 per row (optional intermediate)
                  md={12} // 2 per row (768–991px)
                  lg={8} // 3 per row (992–1439px)
                  xl={8} // 3 per row (≥1440px)
                  xxl={6}
                >
                  <Card
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      ...(store.style || {}),
                    }}
                    styles={{
                      body: {
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        padding: isMobile ? 12 : 16,
                        paddingBottom: 0,
                      },
                    }}
                  >
                    <h3 style={{ marginBottom: 0, marginTop: 0 }}>
                      {store.label}
                    </h3>

                    <p
                      style={{
                        margin: 0,
                        color: "#6B7280",
                        fontSize: 12,
                      }}
                    >
                      <b>{store.company}</b>
                    </p>

                    <div
                      style={{
                        marginTop: 6,
                        display: "-webkit-box",
                        WebkitLineClamp: isMobile ? 3 : 5,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {store.address}
                    </div>

                    <Space
                      direction="horizontal"
                      size={4}
                      style={{ marginTop: 8, width: "100%" }}
                    >
                      <PhoneOutlined /> {store.phoneNumber}
                    </Space>

                    <Space
                      direction="horizontal"
                      size={4}
                      style={{ marginTop: 8, width: "100%" }}
                    >
                      <ClockCircleOutlined /> {store.hours}
                    </Space>

                    <Flex
                      gap={8}
                      key="actions"
                      justify="flex-start"
                      wrap="wrap"
                      style={{
                        marginTop: "auto",
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: 8,
                        paddingTop: 12,
                        marginBottom: 8,
                      }}
                    >
                      {/* <Button
                        type="text"
                        icon={
                          <img
                            src={WazeIcon}
                            alt="Waze"
                            style={{
                              width: 22,
                              height: 22,
                              objectFit: "contain",
                            }}
                          />
                        }
                        style={{ height: 35, background: "#ececec" }}
                        onClick={() => {
                          const name = store.label;
                          const address = store.address.props.children;
                          const query = `${name}, ${address}`;
                          const encoded = encodeURIComponent(query);
                          const url = `https://waze.com/ul?q=${encoded}&navigate=yes`;
                          window.open(url, "_blank");
                        }}
                      /> */}

                      <Button
                        type="text"
                        icon={
                          <img
                            src={GoogleMapsIcon}
                            alt="Google Maps"
                            style={{
                              width: 22,
                              height: 22,
                              objectFit: "contain",
                            }}
                          />
                        }
                        style={{ height: 35, background: "#ececec" }}
                        onClick={() => {
                          const name = store.label;
                          const address = store.address.props.children;
                          const query = `${name}, ${address}`;
                          const encoded = encodeURIComponent(query);
                          const url = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
                          window.open(url, "_blank");
                        }}
                      />
                    </Flex>
                  </Card>
                </Col>
              ))}

              {stores.length === 0 && (
                <Col span={24}>
                  <p style={{ margin: 0, color: "#6B7280" }}>
                    No stores available in this state yet.
                  </p>
                </Col>
              )}
            </Row>
          </Space>
        </Col>
      </Row>
    </Space>
  );
};

export default StoreLocations;
